import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { listIndustries } from '../services/industries';
import { listSoftware } from '../services/software';
import { listTests } from '../services/tests';

const TalentPoolFilterScreen: React.FC = () => {
    const [industry, setIndustry] = useState<number | undefined>(undefined);
    const [software, setSoftware] = useState<number | undefined>(undefined);
    const [test, setTest] = useState<number | undefined>(undefined);
    const [industries, setIndustries] = useState<{ id: number; name: string }[]>([]);
    const [softwareOptions, setSoftwareOptions] = useState<{ id: number; name: string }[]>([]);
    const [testOptions, setTestOptions] = useState<{ id: number; name: string }[]>([]);
    const navigate = useNavigate();

    useEffect(() => {
        (async () => {
            try {
                const industriesData = await listIndustries();
                setIndustries(industriesData);
            } catch (err) {
                setIndustries([]);
            }
        })();
    }, []);

    useEffect(() => {
        if (!industry) {
            setSoftwareOptions([]);
            setSoftware(undefined);
            setTestOptions([]);
            setTest(undefined);
            return;
        }
        (async () => {
            try {
                const swData = await listSoftware(industry);
                setSoftwareOptions(swData);
            } catch (err) {
                setSoftwareOptions([]);
            }
        })();
    }, [industry]);

    useEffect(() => {
        if (!industry) {
            setTestOptions([]);
            setTest(undefined);
            return;
        }
        (async () => {
            try {
                const testData = await listTests(industry, software);
                setTestOptions(
                    testData
                        .filter(t => typeof t.id === 'number' && t.name)
                        .map(t => ({ id: t.id as number, name: t.name }))
                );
            } catch (err) {
                setTestOptions([]);
            }
        })();
    }, [industry, software]);

    const handleSeeAllResults = () => {
        // Pass selected filters as state props to TalentPool screen
        const state: any = {};
        if (industry) state.industry = industry;
        if (software) state.software = software;
        if (test) state.test = test;
        console.log('Navigating with state:', state);
        navigate('/talent-pool', { state: Object.keys(state).length ? state : undefined });
    };

    return (
        <div style={{ display: 'flex', flexDirection: 'column', position: 'relative' }}>
            <div style={{ marginBottom: 0, maxWidth: '100%' }}>
                <div className="d-flex align-items-center justify-content-between mb-4">
                    <h4 style={{ fontWeight: 700, color: '#23244A', fontSize: 28, margin: 0, letterSpacing: '-1px' }}>Talent pool</h4>
                </div>
            </div>
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-start', position: 'relative' }}>
                <div style={{ background: '#fff', borderRadius: 24, boxShadow: '0 2px 16px #E5EAF2', padding: '48px 32px 32px 32px', minWidth: 480, maxWidth: 500, width: '100%', textAlign: 'center', marginTop: '8vh', marginBottom: 40 }}>
                    <h1 style={{ fontWeight: 500, color: '#23244A', fontSize: 16, marginBottom: 15, marginTop: 0, textAlign: 'left' }}>Select industry</h1>
                    <select
                        required
                        className="form-control"
                        value={industry || ""}
                        onChange={e => { setIndustry(e.target.value ? Number(e.target.value) : undefined); setSoftware(undefined); setTest(undefined); }}
                        style={{
                            width: '100%',
                            marginBottom: 24,
                            borderRadius: 28,
                            background: industries.length === 0 ? '#e5e7eb' : '#EAF2FF',
                            fontSize: 16,
                            height: 48,
                            textAlign: 'left',
                            color: industries.length === 0 ? '#a3a3a3' : '#2563eb',
                            border: 'none',
                        }}
                        disabled={industries.length === 0}
                    >
                        <option value="">Industry</option>
                        {industries.map(opt => (
                            <option key={opt.id} value={opt.id}>{opt.name}</option>
                        ))}
                    </select>
                    <h1 style={{ fontWeight: 500, color: '#23244A', fontSize: 16, marginBottom: 15, marginTop: 0, textAlign: 'left' }}>Select software</h1>
                    <select
                        className="form-control"
                        value={software || ""}
                        onChange={e => { setSoftware(e.target.value ? Number(e.target.value) : undefined); setTest(undefined); }}
                        style={{
                            width: '100%',
                            marginBottom: 24,
                            borderRadius: 28,
                            background: (!industry || softwareOptions.length === 0) ? '#e5e7eb' : '#EAF2FF',
                            fontSize: 16,
                            height: 48,
                            textAlign: 'left',
                            color: (!industry || softwareOptions.length === 0) ? '#a3a3a3' : '#2563eb',
                            border: 'none',
                        }}
                        disabled={!industry || softwareOptions.length === 0}
                    >
                        <option value="">Software</option>
                        {softwareOptions.map(opt => (
                            <option key={opt.id} value={opt.id}>{opt.name}</option>
                        ))}
                    </select>
                    <h1 style={{ fontWeight: 500, color: '#23244A', fontSize: 16, marginBottom: 15, marginTop: 0, textAlign: 'left' }}>Select assessment</h1>
                    <select
                        className="form-control"
                        value={test || ""}
                        onChange={e => setTest(e.target.value ? Number(e.target.value) : undefined)}
                        style={{
                            width: '100%',
                            marginBottom: 24,
                            borderRadius: 28,
                            background: (!industry || testOptions.length === 0) ? '#e5e7eb' : '#EAF2FF',
                            fontSize: 16,
                            height: 48,
                            textAlign: 'left',
                            color: (!industry || testOptions.length === 0) ? '#a3a3a3' : '#2563eb',
                            border: 'none',
                        }}
                        disabled={!industry || testOptions.length === 0}
                    >
                        <option value="">Assessment</option>
                        {testOptions.map(opt => (
                            <option key={opt.id} value={opt.id}>{opt.name}</option>
                        ))}
                    </select>
                    <div style={{ marginBottom: 0 }}>
                        <span
                            onClick={handleSeeAllResults}
                            style={{
                                cursor: 'pointer',
                                color: '#2563eb',
                                fontWeight: 500,
                                fontSize: 16,
                                padding: 0,
                                display: 'inline-block',
                            }}
                        >
                            See all results
                        </span>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default TalentPoolFilterScreen;